import { UploadOutlined, UserAddOutlined } from '@ant-design/icons';
import { Input, Drawer, Button, Space, DrawerProps, notification, Upload, message } from 'antd';
import { CountryPhoneInputValue } from 'antd-country-phone-input';
import { UploadFile } from 'antd/lib/upload/interface';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { MAX_SIZE_2MB } from '../consts';
import useSaveCandidateMutation from '../hooks/useSaveCandidateMutation';
import { CandidateCollection, JobPostingDetail } from '../types';
import { handlePossiblePhoneNumberValidation } from '../validators/InternationalPhoneInputValidators';
import InternationalPhoneInput from './ApplicantAnalysis/InternationalPhoneInput';
import Form from './Form';

interface AddCandidateFormValues {
  name: string;
  email: string;
  phoneNumber: CountryPhoneInputValue;
  linkedIn: string;
  annotation: string;
  attachment?: UploadFile[];
}

interface AddCandidateDrawerProps extends DrawerProps {
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: CandidateCollection['id'],
    variables: AddCandidateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const mimeTypes = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

const supportedFiles = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.png',
  '.jpeg',
  '.webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/png',
  'image/jpeg',
  'image/webp',
];

const AddCandidateDrawer = ({ jobPostingId, onSuccess, ...props }: AddCandidateDrawerProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const saveCandidateMutation = useSaveCandidateMutation();
  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      { name, email, linkedIn, phoneNumber, annotation, attachment }: AddCandidateFormValues,
      api
    ) => {
      const candidate = await api.post('/candidates/', {
        name,
        email,
        phone_contacts: phoneNumber.phone
          ? [
              {
                type: 'PERSONAL',
                number: phoneNumber.phone,
                country_code: phoneNumber.code ?? 55,
              },
            ]
          : [],
        social_networks: linkedIn
          ? [
              {
                type: 'LINKEDIN',
                profile: linkedIn,
              },
            ]
          : undefined,
      });

      if (attachment && attachment.length > 0) {
        await saveCandidateMutation.mutateAsync({
          id: candidate.data.id,
          attachment: attachment[0].originFileObj,
        });
      }

      if (annotation) {
        await api.post('/candidate-commentary/', {
          commentary: annotation,
          type: 'NEUTRAL',
          candidate: candidate.data.id,
        });
      }

      if (jobPostingId) {
        const stages = await api.get('/stages/', {
          params: {
            job_posting_id: jobPostingId,
            type: 'SELECTED',
          },
        });

        await api.post('/recruitment-processes/', {
          candidate: candidate.data.id,
          stage: stages.data.results[0].id,
          job_posting: jobPostingId,
          candidate_source_option: 'Adicionado manualmente',
          status: 'ACTIVE',
          subscription_type: 'SELECTED',
        });
      }

      return candidate.data.id;
    },
    {
      onSuccess,
      onError: (err, variables, context) => {
        if (err?.response?.data?.email?.error === 'email_already_exists') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t(
              'Já existe um candidato com esse endereço de e-mail. Por favor confira no Banco de Talentos.'
            ),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries('/candidates/');
        await queryClient.invalidateQueries('/candidate-history/');
        await queryClient.invalidateQueries(['questionnaireUserAnswers']);
      },
    }
  );

  return (
    <Drawer
      title={t('Adicionar candidato')}
      width={720}
      footer={
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<UserAddOutlined />}
            loading={mutation.isLoading}
            onClick={() => form.submit()}
          >
            {' '}
            <Trans>Adicionar</Trans>
          </Button>
        </Space>
      }
      {...props}
    >
      <Helmet>
        <title>
          {t('Adicionar candidato')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<AddCandidateFormValues>
        form={form}
        initialValues={{ phoneNumber: { short: 'br', phone: '' } }}
        layout="vertical"
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Form.Item
          label={t('Nome')}
          name="name"
          rules={[
            { required: true, type: 'string', whitespace: true },
            {
              validator: (rule, value: string) => {
                if (value && value.split(' ').filter((v) => v).length < 2) {
                  return Promise.reject(
                    new Error(t('Por favor, insira o nome completo do candidato.'))
                  );
                }

                if (value && value.match(/[\d@]/g)) {
                  return Promise.reject(
                    new Error(t('Caracteres inválidos, por favor verifique a escrita.'))
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label={t('E-mail')} name="email" rules={[{ type: 'email', required: true }]}>
          <Input placeholder={t('exemplo@email.com')} />
        </Form.Item>
        <Form.Item
          label={t('Telefone')}
          name="phoneNumber"
          hasFeedback
          validateFirst
          validateTrigger="onChange"
          rules={[
            {
              validator: handlePossiblePhoneNumberValidation,
              message: t('Número de telefone inválido'),
            },
          ]}
        >
          <InternationalPhoneInput maxLength={20} />
        </Form.Item>
        <Form.Item label="LinkedIn" name="linkedIn" rules={[{ type: 'url' }]}>
          <Input placeholder={`https://www.linkedin.com/in/${t('exemplo')}`} />
        </Form.Item>
        <Form.Item label={t('Comentário')} name="annotation">
          <Input.TextArea maxLength={10000} autoSize={{ minRows: 5, maxRows: 12 }} />
        </Form.Item>
        <Form.Item
          label={t('Currículo')}
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            maxCount={1}
            listType="text"
            accept={supportedFiles.join(',')}
            beforeUpload={(file: UploadFile) => {
              if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                if (!file.size) {
                  message.error(t('Erro com o arquivo.'));
                  return Upload.LIST_IGNORE;
                }
                if (file.size >= MAX_SIZE_2MB) {
                  message.error(
                    t('Por favor, selecione um arquivo de até 2Mb no campo Currículo.')
                  );
                  return Upload.LIST_IGNORE;
                }

                return false;
              } else {
                message.error(
                  t(
                    'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                  )
                );
                return Upload.LIST_IGNORE;
              }
            }}
          >
            <Button icon={<UploadOutlined />}>
              {' '}
              <Trans>Anexar arquivo</Trans>
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddCandidateDrawer;
